import type { ReactNode } from 'react';
import { Navigate, useLocation, type Location } from 'react-router';
import { errorText } from './errors';
import { t } from './i18n';
import { useSession } from './session';

// The routes' guard (ARCHITECTURE → The web app's structure; D22): a route says who it is for,
// and the guard sends anyone else where the session says they belong. Navigation follows the
// session: a login, a password change or a logout changes it, and the guard moves the member on.
//
// - `signedIn`: the shell's screens. Logged out → /login (and back here after the login, unless
//   the member logged out: then Home, D28); a one-time password to replace → /change-password.
// - `admin`: the same, and a member who isn't an admin → /.
// - `signedOut`: the login. Logged in → where they were going, or /; or the forced change.
// - `mustChange`: the forced change. Logged out → /login; nothing to change → /.
export type Need = 'signedIn' | 'admin' | 'signedOut' | 'mustChange';

/** Where the guard found the member: the login sends them back there. */
type FromState = { from?: unknown };

function here(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/** The path the guard sent the member from, if it's one of ours. */
function cameFrom(location: Location): string | undefined {
  const from = (location.state as FromState | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
    ? from
    : undefined;
}

export function Gate({ need, children }: { need: Need; children: ReactNode }) {
  const { state } = useSession();
  const location = useLocation();

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'failed') return <StartFailed error={state.error} />;
  if (state.status === 'signedOut') {
    if (need === 'signedOut') return children;
    // After a Log out the next login opens Home, wherever it's asked from: it's often someone
    // else's (D28). A session that ended by itself comes back here.
    const from = need === 'mustChange' || state.afterLogout ? undefined : { from: here(location) };
    return <Navigate to="/login" replace state={from} />;
  }

  const { member } = state;
  if (member.mustChangePassword) {
    return need === 'mustChange' ? children : <Navigate to="/change-password" replace />;
  }
  if (need === 'signedOut') return <Navigate to={cameFrom(location) ?? '/'} replace />;
  if (need === 'mustChange') return <Navigate to="/" replace />;
  if (need === 'admin' && member.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

/** While the start's `GET /api/auth/me` is on its way: nothing to see, a word for screen readers. */
function Loading() {
  return (
    <main className="start" aria-busy="true">
      <p className="visually-hidden">{t('app.loading')}</p>
    </main>
  );
}

/** The start's call failed (no connection, or the server's error): say so, and try again. */
function StartFailed({ error }: { error: unknown }) {
  const { retry } = useSession();
  return (
    <main className="start">
      <p className="error-line" role="alert">
        {errorText(error)}
      </p>
      <button type="button" className="button" onClick={retry}>
        {t('app.retry')}
      </button>
    </main>
  );
}
